import setFlexPaneVisibility from '../actions/setFlexPaneVisibility';
import store from '../store/store';
import mailStrings from 'owa-strings/owa-mail-strings';
import { observer } from 'mobx-react';
import { Panel, IPanelProps } from 'office-ui-fabric-react/lib/Panel';
import { lazyTriggerResizeEvent } from 'owa-resize-event';
import * as React from 'react';
import * as ReactDOM from 'react-dom';

const styles = require('./FlexPane.scss');
const classNames = require('classnames/bind').bind(styles);

export interface FlexPaneOptions {
    /**
     * Whether to turn into a modal Panel with soft dismiss on small-width screens (currently < 1400px)
     * @default true
     */
    overlayOnSmallWidth?: boolean;

    /**
     * Optional CSS class name to use to style the panel
     * @default ""
     */
    className?: string;

    /**
     * Optional text to display in the header section of the panel
     * @default ""
     */
    headerText?: (() => string) | string | (() => JSX.Element);

    /**
     * Optional CSS class name to use to style the header text
     * @default ""
     */
    headerClassName?: string;

    /**
     * Optional callback onDismiss
     */
    onDismiss?: () => any;
}

/**
 * This component is broken out so that it will re-render if headerText is a () => string,
 * and the observable string changes.
 * Otherwise, the Fabric Panel isn't observable, and the OWA FlexPane.render() method doesn't
 * directly dereference header text, so it would never rerender.
 */
const FlexPaneHeader = observer((props: FlexPaneOptions) => {
    let headerText =
        props.headerText &&
        (typeof props.headerText === 'string' ? props.headerText : props.headerText());

    return (
        <span className={styles.headerText} role="heading" aria-level={2}>
            {headerText}
        </span>
    );
});

@observer
class FlexPane extends React.Component<FlexPaneOptions, {}> {
    private previousIsShown: boolean;

    render() {
        return (
            <Panel
                isOpen={store.isShown}
                isBlocking={this.props.overlayOnSmallWidth}
                forceFocusInsideTrap={false}
                onDismiss={this.onFlexPaneDismiss.bind(this)}
                headerClassName={this.props.headerClassName}
                className={classNames(styles.flexPane, this.props.className, {
                    overlay: this.props.overlayOnSmallWidth,
                })}
                closeButtonAriaLabel={mailStrings.closeButton}
                isLightDismiss={true}
                onRenderNavigation={this.onRenderHeader}>
                {/*
                        We can work around the limitations of Panel blocking interaction with the underlying surface by setting 'isLightDismiss' to true,
                        and resizing the underlaying Layer using CSS. However, this feels like a hack, so I've opened an issue against Fabric to investigate
                        other means of accomplishing our goal.
                        https://github.com/OfficeDev/office-ui-fabric-react/issues/1575
                    */}
                {this.props.children}
            </Panel>
        );
    }

    public componentDidMount() {
        this.previousIsShown = store.isShown;
        if (this.previousIsShown) {
            lazyTriggerResizeEvent.importAndExecute();
        }
    }

    public componentDidUpdate() {
        if (this.previousIsShown != store.isShown) {
            lazyTriggerResizeEvent.importAndExecute();
            this.previousIsShown = store.isShown;
        }
    }

    private onFlexPaneDismiss = () => {
        setFlexPaneVisibility(false);

        // check if panel closing has been initiated by explicit action
        if (!isExternalHide) {
            activePaneCount--;
        }

        // reset the flag
        isExternalHide = false;

        if (this.props.onDismiss) {
            this.props.onDismiss();
        }
    };

    private onRenderHeader = (
        props?: IPanelProps,
        defaultRender?: (props?: IPanelProps) => JSX.Element | null
    ) => {
        return (
            <div className={styles.header}>
                <FlexPaneHeader {...this.props} />
                {defaultRender(props)}
            </div>
        );
    };
}

let flexPaneContainer: HTMLDivElement;

/*
    'activePaneCount' counts how many times flex pane has been opened.
    These is needed when open and hide requests can go out of order.
    'isExternalHide' indicates whether hide was initiated throught action.
    This is used to prevent double counting the hides.
*/
let activePaneCount: number = 0;
let isExternalHide = false;

export function mountAndShowFlexPane(content: JSX.Element, options?: FlexPaneOptions) {
    if (!flexPaneContainer) {
        flexPaneContainer = document.createElement('div');
        document.body.appendChild(flexPaneContainer);
    }

    let props = {
        ...{
            overlayOnSmallWidth: true,
        },
        ...options,
    };

    ReactDOM.render(<FlexPane {...props}>{content}</FlexPane>, flexPaneContainer);

    setFlexPaneVisibility(true, props.overlayOnSmallWidth);
    activePaneCount++;
}

export function hideFlexPane() {
    isExternalHide = true;

    if (activePaneCount > 1) {
        activePaneCount--;
    } else {
        setFlexPaneVisibility(false);
        activePaneCount = 0;
    }
}
